import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface PortalGorunum {
  gorunumID: number;
  gorunumAdi: string;
  gorselUrl: string;
  isaretler: { xYuzde: number; yYuzde: number; rxMin?: number | null; ryMin?: number | null; rxMax?: number | null; ryMax?: number | null }[];
}

interface Props {
  gorunumler: PortalGorunum[];
  apiBase: string;
}

const MIN_OLCEK = 1;
const MAX_OLCEK = 6;
const RENK = '#e67d21';

interface Donusum { s: number; tx: number; ty: number; }

const sinirla = (d: Donusum, w: number, h: number): Donusum => {
  const s = Math.min(MAX_OLCEK, Math.max(MIN_OLCEK, d.s));
  return {
    s,
    tx: Math.min(0, Math.max(w - s * w, d.tx)),
    ty: Math.min(0, Math.max(h - s * h, d.ty)),
  };
};

// Görsel kendi en-boy oranında gösterilir; iki parmakla yakınlaştırma, yakınken sürükleme,
// çift dokunuşla büyütme. Masaüstünde butonlar, sürükleme ve ctrl+tekerlek (trackpad pinch).
const ZoomSahne: React.FC<{ gorunum: PortalGorunum; apiBase: string; oran: number; onOran: (o: number) => void; tamEkran?: boolean }> =
  ({ gorunum, apiBase, oran, onOran, tamEkran }) => {
  const sahneRef = useRef<HTMLDivElement>(null);
  const [d, setD] = useState<Donusum>({ s: 1, tx: 0, ty: 0 });
  const dRef = useRef(d);
  dRef.current = d;
  const jest = useRef<{ tip: 'pinch' | 'pan' | null; d0: Donusum; mesafe0: number; orta0: { x: number; y: number }; p0: { x: number; y: number } }>(
    { tip: null, d0: { s: 1, tx: 0, ty: 0 }, mesafe0: 1, orta0: { x: 0, y: 0 }, p0: { x: 0, y: 0 } }
  );
  const sonDokunus = useRef(0);

  useEffect(() => { setD({ s: 1, tx: 0, ty: 0 }); }, [gorunum.gorunumID]);

  const boyut = () => {
    const r = sahneRef.current!.getBoundingClientRect();
    return { r, w: r.width, h: r.height };
  };

  const odaktaYakinlastir = useCallback((yeniS: number, fx: number, fy: number) => {
    const { w, h } = boyut();
    const cur = dRef.current;
    const cx = (fx - cur.tx) / cur.s;
    const cy = (fy - cur.ty) / cur.s;
    setD(sinirla({ s: yeniS, tx: fx - yeniS * cx, ty: fy - yeniS * cy }, w, h));
  }, []);

  // Dokunmatik olaylar pasif olmayan dinleyiciyle bağlanır; aksi halde tarayıcı sayfayı yakınlaştırır.
  useEffect(() => {
    const el = sahneRef.current;
    if (!el) return;
    const yerel = (t: Touch, r: DOMRect) => ({ x: t.clientX - r.left, y: t.clientY - r.top });

    const basla = (e: TouchEvent) => {
      const { r } = boyut();
      if (e.touches.length === 2) {
        const a = yerel(e.touches[0], r);
        const b = yerel(e.touches[1], r);
        jest.current = { ...jest.current, tip: 'pinch', d0: dRef.current, mesafe0: Math.hypot(a.x - b.x, a.y - b.y) || 1, orta0: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
      } else if (e.touches.length === 1) {
        const p = yerel(e.touches[0], r);
        const simdi = Date.now();
        if (simdi - sonDokunus.current < 300) {
          e.preventDefault();
          const cur = dRef.current;
          odaktaYakinlastir(cur.s > 1.05 ? 1 : 2.5, p.x, p.y);
          sonDokunus.current = 0;
          jest.current.tip = null;
          return;
        }
        sonDokunus.current = simdi;
        jest.current = { ...jest.current, tip: dRef.current.s > 1 ? 'pan' : null, d0: dRef.current, p0: p };
      }
    };

    const hareket = (e: TouchEvent) => {
      const { r, w, h } = boyut();
      const j = jest.current;
      if (e.touches.length === 2 && j.tip === 'pinch') {
        e.preventDefault();
        const a = yerel(e.touches[0], r);
        const b = yerel(e.touches[1], r);
        const mesafe = Math.hypot(a.x - b.x, a.y - b.y);
        const orta = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const s = Math.min(MAX_OLCEK, Math.max(MIN_OLCEK, j.d0.s * (mesafe / j.mesafe0)));
        const cx = (j.orta0.x - j.d0.tx) / j.d0.s;
        const cy = (j.orta0.y - j.d0.ty) / j.d0.s;
        setD(sinirla({ s, tx: orta.x - s * cx, ty: orta.y - s * cy }, w, h));
      } else if (e.touches.length === 1 && j.tip === 'pan') {
        e.preventDefault();
        const p = yerel(e.touches[0], r);
        setD(sinirla({ s: j.d0.s, tx: j.d0.tx + p.x - j.p0.x, ty: j.d0.ty + p.y - j.p0.y }, w, h));
      }
    };

    const bitir = (e: TouchEvent) => {
      if (e.touches.length === 1 && dRef.current.s > 1) {
        const { r } = boyut();
        jest.current = { ...jest.current, tip: 'pan', d0: dRef.current, p0: yerel(e.touches[0], r) };
      } else if (e.touches.length === 0) {
        jest.current.tip = null;
      }
    };

    const tekerlek = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const { r } = boyut();
      odaktaYakinlastir(dRef.current.s * Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top);
    };

    el.addEventListener('touchstart', basla, { passive: false });
    el.addEventListener('touchmove', hareket, { passive: false });
    el.addEventListener('touchend', bitir);
    el.addEventListener('touchcancel', bitir);
    el.addEventListener('wheel', tekerlek, { passive: false });
    return () => {
      el.removeEventListener('touchstart', basla);
      el.removeEventListener('touchmove', hareket);
      el.removeEventListener('touchend', bitir);
      el.removeEventListener('touchcancel', bitir);
      el.removeEventListener('wheel', tekerlek);
    };
  }, [odaktaYakinlastir]);

  // Masaüstü: yakınken fareyle sürükleme
  const fareBaslangic = useRef<{ x: number; y: number; d0: Donusum } | null>(null);
  const fareAsagi = (e: React.MouseEvent) => {
    if (dRef.current.s <= 1) return;
    fareBaslangic.current = { x: e.clientX, y: e.clientY, d0: dRef.current };
  };
  const fareHareket = (e: React.MouseEvent) => {
    const f = fareBaslangic.current;
    if (!f) return;
    const { w, h } = boyut();
    setD(sinirla({ s: f.d0.s, tx: f.d0.tx + e.clientX - f.x, ty: f.d0.ty + e.clientY - f.y }, w, h));
  };
  const fareBirak = () => { fareBaslangic.current = null; };

  // Butonlar dairenin işaretine doğru yakınlaştırır; işaret yoksa merkeze.
  const butonla = (carpan: number) => {
    const { w, h } = boyut();
    const cur = dRef.current;
    const m = gorunum.isaretler[0];
    const fx = m ? cur.tx + cur.s * (m.xYuzde / 100) * w : w / 2;
    const fy = m ? cur.ty + cur.s * (m.yYuzde / 100) * h : h / 2;
    odaktaYakinlastir(cur.s * carpan, fx, fy);
  };

  const ust = 1 / d.s;
  const btn: React.CSSProperties = {
    width: 36, height: 36, borderRadius: 10, border: 'none', background: 'rgba(15,23,42,0.72)', color: '#fff',
    fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div ref={sahneRef}
        onMouseDown={fareAsagi} onMouseMove={fareHareket} onMouseUp={fareBirak} onMouseLeave={fareBirak}
        style={{
          position: 'relative', width: '100%', aspectRatio: String(oran), overflow: 'hidden', borderRadius: tamEkran ? 0 : 12,
          background: '#0f172a', touchAction: d.s > 1 ? 'none' : 'pan-y', userSelect: 'none',
          cursor: d.s > 1 ? (fareBaslangic.current ? 'grabbing' : 'grab') : 'default',
        }}>
        <div style={{ position: 'absolute', inset: 0, transformOrigin: '0 0', transform: `translate(${d.tx}px, ${d.ty}px) scale(${d.s})`, willChange: 'transform' }}>
          <img src={`${apiBase}${gorunum.gorselUrl}`} alt={gorunum.gorunumAdi} draggable={false}
            onLoad={e => { const im = e.currentTarget; if (im.naturalWidth && im.naturalHeight) onOran(im.naturalWidth / im.naturalHeight); }}
            style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }} />
          {gorunum.isaretler.map((m, i) => (
            <React.Fragment key={i}>
              {m.rxMin != null && m.ryMin != null && m.rxMax != null && m.ryMax != null && (
                <div style={{
                  position: 'absolute', left: `${m.rxMin}%`, top: `${m.ryMin}%`, width: `${m.rxMax - m.rxMin}%`, height: `${m.ryMax - m.ryMin}%`,
                  border: `${2 * ust}px solid ${RENK}`, borderRadius: 4 * ust, background: 'rgba(230,125,33,0.18)', boxSizing: 'border-box',
                }} />
              )}
              <div style={{ position: 'absolute', left: `${m.xYuzde}%`, top: `${m.yYuzde}%`, transform: `translate(-50%, -50%) scale(${ust})`, zIndex: 2 }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: RENK, border: '3px solid #fff', boxShadow: `0 0 0 2px ${RENK}, 0 2px 10px rgba(0,0,0,0.45)` }} />
                {i === 0 && (
                  <div style={{ position: 'absolute', left: '50%', bottom: 'calc(100% + 8px)', transform: 'translateX(-50%)', background: '#0f172a', color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 7, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.35)' }}>
                    Daireniz
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div style={{ position: 'absolute', right: 8, bottom: 8, display: 'flex', flexDirection: 'column', gap: 6, zIndex: 5 }}>
          <button type="button" aria-label="Yakınlaştır" style={btn} onClick={() => butonla(1.6)}><i className="fas fa-plus" /></button>
          <button type="button" aria-label="Uzaklaştır" style={btn} onClick={() => butonla(1 / 1.6)}><i className="fas fa-minus" /></button>
          {d.s > 1 && <button type="button" aria-label="Sıfırla" style={btn} onClick={() => setD({ s: 1, tx: 0, ty: 0 })}><i className="fas fa-compress" /></button>}
        </div>
      </div>
    </div>
  );
};

const PortalGorunumViewer: React.FC<Props> = ({ gorunumler, apiBase }) => {
  const [aktif, setAktif] = useState(0);
  const [oranlar, setOranlar] = useState<Record<number, number>>({});
  const [tamEkran, setTamEkran] = useState(false);

  // Tam ekranda arkadaki sayfa kaymasın
  useEffect(() => {
    if (!tamEkran) return;
    const onceki = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = onceki; };
  }, [tamEkran]);

  if (gorunumler.length === 0) return null;
  const g = gorunumler[Math.min(aktif, gorunumler.length - 1)];
  const oran = oranlar[g.gorunumID] || 16 / 9;
  const oranKaydet = (o: number) => setOranlar(prev => (prev[g.gorunumID] === o ? prev : { ...prev, [g.gorunumID]: o }));

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2440' }}>
          <i className="fas fa-map-marker-alt" style={{ marginRight: 6, color: RENK }} />Dairenizin Konumu
        </div>
        <button type="button" onClick={() => setTamEkran(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 11px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
          <i className="fas fa-expand" /> Tam Ekran
        </button>
      </div>

      {gorunumler.length > 1 && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 4 }}>
          {gorunumler.map((x, i) => (
            <button key={x.gorunumID} type="button" onClick={() => setAktif(i)}
              style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${i === aktif ? RENK : '#e2e8f0'}`, background: i === aktif ? '#fff7ed' : '#fff', color: i === aktif ? '#c2410c' : '#475569', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              {x.gorunumAdi}
            </button>
          ))}
        </div>
      )}

      <ZoomSahne gorunum={g} apiBase={apiBase} oran={oran} onOran={oranKaydet} />
      <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6, textAlign: 'center' }}>
        İki parmakla yakınlaştırabilir, çift dokunarak büyütebilirsiniz.
      </div>

      {tamEkran && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: '#020617', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', color: '#fff' }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{g.gorunumAdi}</div>
            <button type="button" onClick={() => setTamEkran(false)} aria-label="Kapat"
              style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 18, cursor: 'pointer' }}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 0 16px' }}>
            {/* Ekrana sığacak en büyük boyut — görselin oranı korunur */}
            <div style={{ width: `min(100vw, calc((100vh - 90px) * ${oran}))` }}>
              <ZoomSahne gorunum={g} apiBase={apiBase} oran={oran} onOran={oranKaydet} tamEkran />
            </div>
          </div>
          {gorunumler.length > 1 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '0 14px 16px' }}>
              {gorunumler.map((x, i) => (
                <button key={x.gorunumID} type="button" onClick={() => setAktif(i)}
                  style={{ flexShrink: 0, padding: '7px 13px', borderRadius: 20, border: 'none', background: i === aktif ? RENK : 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                  {x.gorunumAdi}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalGorunumViewer;
